import { useQuery } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { CommentsCountsQuery } from './useCommentsCounts'

type UseUnrepliedCountReturn = {
  unrepliedCount: number | undefined
  totalCommentCount: number | undefined
  loading: boolean
}

const sumCounts = (
  counts: readonly { count: number }[] | undefined,
): number | undefined =>
  counts?.reduce((sum, count) => sum + (count.count || 0), 0)

/**
 * Fetches the total number of unreplied comments and the total comment count
 * across the given channels. Used to determine whether to show engagement-based
 * empty states when the "all" filter is active, and to distinguish "channel
 * never received comments" from "all comments replied".
 *
 * Apollo handles request sequencing, caching, and error state — stale responses
 * from previous channel selections are automatically superseded.
 *
 * @param channelIds - Channel IDs to count comments for. Pass an empty array
 *   to skip the query entirely.
 */
export function useUnrepliedCount(
  channelIds: string[],
): UseUnrepliedCountReturn {
  const organizationId = useOrganizationId()
  const skip = !organizationId || channelIds.length === 0

  const unrepliedQuery = useQuery(CommentsCountsQuery, {
    skip,
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        organizationId: organizationId ?? '',
        filters: { channelIds, status: ['unreplied'] },
      },
    },
  })

  const totalQuery = useQuery(CommentsCountsQuery, {
    skip,
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        organizationId: organizationId ?? '',
        filters: { channelIds },
      },
    },
  })

  // Treat error or missing payload as "unknown" (undefined), not zero,
  // so the UI doesn't incorrectly show "all done" / "listening" states.
  const unrepliedCount = unrepliedQuery.error
    ? undefined
    : sumCounts(unrepliedQuery.data?.commentsCounts?.counts)

  const totalCommentCount = totalQuery.error
    ? undefined
    : sumCounts(totalQuery.data?.commentsCounts?.counts)

  return {
    unrepliedCount,
    totalCommentCount,
    loading: unrepliedQuery.loading || totalQuery.loading,
  }
}
